/**
 * Sentry SDK initialisation for the frontend.
 *
 * MUST be the very first import in `index.js` so the SDK installs its
 * global error handlers before any application code runs. The
 * `DISABLE_SENTRY` and missing-DSN paths are no-ops, so dev / CI / preview
 * runs that don't carry credentials cost nothing.
 *
 * Pairs with the backend `utils/sentry_setup.py` — separate Sentry
 * project per environment so JS errors don't drown backend exceptions
 * in a unified issue feed.
 */

import * as Sentry from '@sentry/react';

const dsn = process.env.REACT_APP_SENTRY_DSN || '';
const disabled = (process.env.REACT_APP_DISABLE_SENTRY || '').toLowerCase() === 'true';
const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production';

// Field names whose VALUES are scrubbed before any breadcrumb / event
// is transmitted. Mirrors the backend list so an attacker can't pull
// them out of either layer.
const SENSITIVE_FIELDS = new Set([
  'email',
  'password',
  'hashed_password',
  'current_password',
  'new_password',
  'jwt_secret_key',
  'authorization',
  'cookie',
  'csrf_token',
  'access_token',
  'ssl_pin',
  'ssl_token',
  'ssl_card_number',
]);

const SENSITIVE_QUERY_PARAMS = ['email', 'password', 'token', 'ssl_token', 'ssl_card_number'];

const redactDict = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  for (const key of Object.keys(obj)) {
    if (SENSITIVE_FIELDS.has(key.toLowerCase())) {
      obj[key] = '[REDACTED]';
    }
  }
  return obj;
};

const redactUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  let out = url;
  for (const param of SENSITIVE_QUERY_PARAMS) {
    out = out.replace(new RegExp(`(${param}=)[^&]*`, 'gi'), '$1[REDACTED]');
  }
  return out;
};

const beforeSend = (event /* , hint */) => {
  try {
    if (event.request) {
      redactDict(event.request.headers);
      redactDict(event.request.cookies);
      event.request.url = redactUrl(event.request.url);
      if (event.request.data && typeof event.request.data === 'object') {
        redactDict(event.request.data);
      }
    }
    redactDict(event.tags);
    redactDict(event.extra);
    if (Array.isArray(event.breadcrumbs)) {
      for (const crumb of event.breadcrumbs) {
        if (crumb.data) {
          redactDict(crumb.data);
          if (crumb.data.url) crumb.data.url = redactUrl(crumb.data.url);
          // Try-parse JSON request bodies attached to fetch/xhr crumbs.
          if (typeof crumb.data.request_body === 'string') {
            try {
              const body = JSON.parse(crumb.data.request_body);
              redactDict(body);
              crumb.data.request_body = JSON.stringify(body);
            } catch {
              // not JSON — leave alone
            }
          }
        }
      }
    }
  } catch {
    // Never let our scrubber break event submission.
  }
  return event;
};

const beforeBreadcrumb = (crumb /* , hint */) => {
  // Last-mile redaction at the point where breadcrumbs enter the buffer
  // (before they're attached to an event). Cheaper than scrubbing every
  // queued crumb at send time when an error fires.
  if (crumb.data) {
    redactDict(crumb.data);
    if (crumb.data.url) crumb.data.url = redactUrl(crumb.data.url);
  }
  return crumb;
};

let initialised = false;

if (dsn && !disabled) {
  try {
    Sentry.init({
      dsn,
      environment,
      sendDefaultPii: false,
      // 10% of transactions in production-like envs to keep budget sane;
      // the SDK still captures 100% of errors regardless of this rate.
      tracesSampleRate: environment === 'production' ? 0.1 : 0.3,
      // Browser-tracing for navigation transactions. Replay is off by
      // default — we can opt in later if a flow needs visual debug.
      integrations: [Sentry.browserTracingIntegration()],
      beforeSend,
      beforeBreadcrumb,
    });
    initialised = true;
    // eslint-disable-next-line no-console
    console.info(`[sentry] initialised (env=${environment})`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed:', err);
  }
}

export const sentryEnabled = () => initialised;
export { Sentry };
